import { Axial, axialKey, distance, neighbor } from '../map/HexCoordinates';
import { HexCellData } from '../map/HexSurface';
import { MAX_STEP, levelOf } from '../map/Movement';
import { BiomeInfo } from '../map/WorldSource';
import { BuildingKind, Catalogue, buildingOf } from './Catalogue';
import { Layer, Objects, labelOfObject } from './Objects';

/**
 * Whether the ground here takes a building, client-side.
 *
 * **Twin of `buildings/site.rs`.** The server decides; this exists so the
 * cursor can say what a click would do before it is clicked.
 *
 * It holds **no numbers and no names**. Radii, drops, thresholds, the objects a
 * building needs beside it and the words for them all arrive in the catalogue
 * and the dictionaries. What is written twice is the reading of three ground
 * rules and four lists — and adding a building, an object or a biome costs
 * nothing on this side.
 */

/** Why not here. The codes match `Refusal` in `site.rs`. */
export type Refusal =
  | 'unknown'
  | 'water'
  | 'steep'
  | 'unreachable'
  | 'wrongField'
  | 'blockedField'
  | 'missingNeighbour'
  | 'blockedNeighbour'
  | 'noWater'
  | 'noEdge'
  | 'unsupported'
  | 'crowded';

/** Out of reach: not a condition of the ground, so not one of the codes. */
export type Nowhere = Refusal | 'far';

/**
 * What already stands on a cell, as a placement rule needs to see it.
 *
 * The object key alone was enough while every rule asked *what* is there. Two
 * of them now ask *whose* and *how far up*: a village is a camp on its second
 * rung and carries the same `OBJ_CAMP`, and a field beside somebody else's
 * field is not a field beside yours. `mine` rather than an owner id, so the
 * rule never learns who anybody is — the caller answers the only question it
 * has. Twin of `Standing` in `site.rs`.
 */
export interface Standing {
  /** What the four lists see — the object this building is. */
  object: string;
  /** The catalogue key, which is what a condition names. */
  key: string;
  level: number;
  /** Whether it belongs to whoever is trying to build. */
  mine: boolean;
}

/**
 * The dictionaries a site rule is read against.
 *
 * Passed in rather than imported, because they are fetched: a rule asked before
 * they arrive would answer from an empty vocabulary and refuse everything.
 */
export interface Vocabulary {
  objects: Objects;
  /** Biome entries by key, as the dictionary is keyed. */
  biomes: Map<string, BiomeInfo>;
  /**
   * The catalogue, because one condition names **buildings** rather than
   * objects — and a rung of one, at that. "Beside a village" has to come out as
   * the word the file uses for a camp's second rung, not as `camp` or as the
   * object it wears.
   */
  catalogue: Catalogue;
}

/** Above this level of woodland or stone nobody gets through. Twin of the server's. */
const PASSABLE_LEVEL = 2;

const underWater = (cell: HexCellData): boolean =>
  cell.elevation < 0 || cell.lakeLevel !== undefined;

/** Every cell within `radius` of a centre, the centre included. */
export function within(at: Axial, radius: number): Axial[] {
  const cells: Axial[] = [];
  for (let q = -radius; q <= radius; q += 1) {
    const from = Math.max(-radius, -q - radius);
    const to = Math.min(radius, -q + radius);
    for (let r = from; r <= to; r += 1) {
      cells.push({ q: at.q + q, r: at.r + r });
    }
  }
  return cells;
}

/**
 * How thick a layer is on one cell, on the one-to-five scale.
 *
 * The river layer is a mask of the edges water crosses rather than a richness,
 * so it answers one or nothing.
 */
function thickness(cell: HexCellData, layer: Layer): number {
  if (layer === 'forest') return levelOf(cell.forest);
  if (layer === 'stone') return levelOf(cell.stone);
  if (layer === 'fish') return levelOf(cell.fish);
  return cell.river ? 1 : 0;
}

/** The richest reading of an object's layer in reach, on the one-to-five scale. */
export function richest(
  world: Map<number, HexCellData>,
  vocabulary: Vocabulary,
  at: Axial,
  object: string,
  radius: number
): number {
  const from = vocabulary.objects.objects[object]?.from;
  if (!from) return 0;

  let best = 0;
  for (const cell of within(at, radius)) {
    const data = world.get(axialKey(cell.q, cell.r));
    if (!data) continue;
    // A deposit is only worked in the element it belongs to: a wood under water
    // is a drowned wood, a shoal on dry land is nothing at all. Which element
    // that is, the dictionary says — see `From.inWater`.
    if (underWater(data) !== !!from.inWater) continue;
    best = Math.max(best, thickness(data, from.layer));
  }
  return best;
}

/**
 * Everything standing on one cell, in the vocabulary the rules are written in.
 *
 * Natural objects come from the world layers, the built one from whoever is
 * standing there, and the biome is in the same list — a rule saying "not on a
 * beach" reads the same way as one saying "not on a river", and the prefix on
 * the key says which dictionary answered.
 */
export function whatIsOn(
  vocabulary: Vocabulary,
  cell: HexCellData,
  standing: Standing | undefined
): string[] {
  const on: string[] = [];

  for (const [key, info] of Object.entries(vocabulary.objects.objects)) {
    if (info.from && thickness(cell, info.from.layer) >= info.from.least) on.push(key);
  }

  if (cell.biome !== undefined) {
    for (const [key, info] of vocabulary.biomes) {
      if (info.id === cell.biome) {
        on.push(key);
        break;
      }
    }
  }

  if (standing) on.push(standing.object);
  return on;
}

const matches = (on: string[], list: string[] | undefined): boolean =>
  !!list && list.some((wanted) => on.includes(wanted));

/**
 * Can somebody stand next to it?
 *
 * The site itself may be a thicket — a lumberjack goes where the wood is, and
 * the wood worth going to is the wood too thick to walk through. What has to
 * hold is that one of the six neighbours can be stood on and is within a step's
 * climb, so the building is not walled in behind its own ground.
 */
function reachable(
  world: Map<number, HexCellData>,
  at: Axial,
  middle: HexCellData
): true | Refusal {
  let unread = false;

  for (let direction = 0; direction < 6; direction += 1) {
    const side = neighbor(at, direction);
    const data = world.get(axialKey(side.q, side.r));
    if (!data) {
      unread = true;
      continue;
    }
    if (underWater(data)) continue;
    if (levelOf(data.forest) > PASSABLE_LEVEL || levelOf(data.stone) > PASSABLE_LEVEL) {
      continue;
    }
    if (Math.abs(data.elevation - middle.elevation) <= MAX_STEP) return true;
  }

  return unread ? 'unknown' : 'unreachable';
}

/**
 * Does the ground within `radius` stay within `maxDrop` of the middle?
 *
 * **Water is skipped**, as it is in `cornerSlope` and in the server's copy of
 * this rule: a water cell carries its bed, so measuring against one reports a
 * cliff at every shoreline.
 */
function flat(
  world: Map<number, HexCellData>,
  at: Axial,
  middle: HexCellData,
  radius: number,
  maxDrop: number
): true | Refusal {
  for (const cell of within(at, radius)) {
    const data = world.get(axialKey(cell.q, cell.r));
    if (!data) return 'unknown';
    if (underWater(data)) continue;
    if (Math.abs(data.elevation - middle.elevation) > maxDrop) return 'steep';
  }
  return true;
}

/**
 * Is any of the six neighbours water?
 *
 * Sea and lake alike, read as facts — a negative elevation and the lake layer —
 * never from the biome. Ground nobody has read is not ground with no water on
 * it, so an unread ring answers `unknown` rather than "no".
 */
function besideWater(world: Map<number, HexCellData>, at: Axial): true | Refusal {
  let unread = false;

  for (let direction = 0; direction < 6; direction += 1) {
    const side = neighbor(at, direction);
    const data = world.get(axialKey(side.q, side.r));
    if (!data) {
      unread = true;
      continue;
    }
    if (underWater(data)) return true;
  }

  return unread ? 'unknown' : 'noWater';
}

/**
 * Does the deposit **end** at this cell — is some neighbour without it?
 *
 * A quarry works the rim rather than the middle of a boulder field: there has
 * to be somewhere to stand and somewhere to cart the stone off to. Whether the
 * cell itself carries the deposit is `allowedField`'s question, not this one.
 */
function onEdgeOf(
  world: Map<number, HexCellData>,
  vocabulary: Vocabulary,
  object: string,
  at: Axial
): true | Refusal {
  const from = vocabulary.objects.objects[object]?.from;
  // A built object is on a cell because somebody put it there, which is not
  // something the ground can be on the edge of. The server refuses such a
  // catalogue at startup, so this is the shape of an answer, not a case.
  if (!from) return 'noEdge';

  let unread = false;

  for (let direction = 0; direction < 6; direction += 1) {
    const side = neighbor(at, direction);
    const data = world.get(axialKey(side.q, side.r));
    if (!data) {
      unread = true;
      continue;
    }
    if (thickness(data, from.layer) < from.least) return true;
  }

  return unread ? 'unknown' : 'noEdge';
}

/**
 * Is one of the six neighbours a building of this player's that the condition
 * accepts?
 *
 * **Level is a lower bound, not an equal.** "Beside a village" is the camp on
 * its second rung, and a third rung would be more of a village, not less.
 *
 * A neighbour whose ground has not arrived counts as unknown rather than as
 * empty: buildings come with the terrain, so "no" would turn into "yes" as the
 * map came in.
 */
function besideOwn(
  world: Map<number, HexCellData>,
  at: Axial,
  any: { building: string; level: number }[],
  standing: (cell: Axial) => Standing | undefined
): true | Refusal {
  let unread = false;

  for (let direction = 0; direction < 6; direction += 1) {
    const side = neighbor(at, direction);
    if (!world.get(axialKey(side.q, side.r))) {
      unread = true;
      continue;
    }

    const built = standing(side);
    if (!built || !built.mine) continue;
    if (any.some((wanted) => wanted.building === built.key && built.level >= wanted.level)) {
      return true;
    }
  }

  return unread ? 'unknown' : 'unsupported';
}

/** Every condition, or the first one that fails. */
/**
 * Czy coś stoi za blisko — licząc w obie strony. Bliźniak `crowding` z `site.rs`.
 *
 * Dwa pytania, jedna pętla, bo odstęp jest **odległością między dwoma
 * budynkami**: odmawia i wtedy, gdy stawiany budynek chce pustki, której nie ma,
 * i wtedy, gdy w zasięgu stoi ktoś, kto pustki wokół siebie pilnuje. Czyje to
 * jest, nie pyta.
 *
 * **Ta strona wie tyle, ile dostała.** Budynek na chunku, który jeszcze nie
 * przyszedł, nie odmówi tutaj i odmówi na serwerze — a pustki, w odróżnieniu od
 * sąsiedztwa, nie da się potwierdzić brakiem danych: „nie ma tam nic" i „nie
 * wiem, co tam jest" czyta się z tej mapy tak samo. Podgląd jest więc podglądem,
 * a odpowiedź serwera obowiązuje.
 */
function crowded(
  vocabulary: Vocabulary,
  kind: BuildingKind,
  at: Axial,
  standing: (cell: Axial) => Standing | undefined
): true | undefined {
  const reach = vocabulary.catalogue.buildings.reduce(
    (most, entry) => Math.max(most, entry.clearance ?? 0),
    0
  );
  if (reach === 0) return undefined;

  const ours = kind.clearance ?? 0;
  // O jeden mniej niż odstęp: siedem pól odstępu znaczy sześć zajętych
  // pierścieni, a na siódmym wolno już stać.
  for (const side of within(at, reach - 1)) {
    if (side.q === at.q && side.r === at.r) continue;
    const there = standing(side);
    if (!there) continue;
    const apart = distance(at, side);
    const theirs = buildingOf(vocabulary.catalogue, there.key)?.clearance ?? 0;
    if (ours > apart || theirs > apart) return true;
  }

  return undefined;
}

/**
 * Które pola zajmuje budynek stojący środkiem na `at`. Bliźniak
 * `site::footprint`.
 *
 * Jedno, dopóki wpis nie mówi inaczej — czyli wszystko poza stocznią. Ze
 * skrzydłami: pole środkowe i po `wings` pól w obie strony wzdłuż osi
 * (krawędź `axis` i przeciwległa `axis + 3`, jak przy moście).
 *
 * **Jedna funkcja, o którą pytają wszyscy po tej stronie**: reguła miejsca,
 * duch pod kursorem, nakładka na polach i wyrównanie terenu. Lista pól
 * wyprowadzona osobno w każdym z tych miejsc rozjechałaby się cicho, bo każda
 * z osobna wygląda na działającą.
 */
export function footprint(kind: BuildingKind, at: Axial, axis: number | undefined): Axial[] {
  if (!kind.wings) return [at];

  const along = (axis ?? 0) % 3;
  const cells = [at];
  for (let step = 1; step <= kind.wings; step += 1) {
    for (const edge of [along, along + 3]) {
      const side = neighbor(at, edge % 6);
      cells.push({ q: at.q + (side.q - at.q) * step, r: at.r + (side.r - at.r) * step });
    }
  }
  return cells;
}

/**
 * Czy grunt bierze ten budynek — **na każdym polu, na którym stanie**.
 *
 * Budynek jednopolowy przechodzi tę pętlę raz i nic o niej nie wie; stocznia
 * sprawdza tak wszystkie trzy pola, bo dok jest jej częścią, a nie widokiem.
 */
export function checkSite(
  world: Map<number, HexCellData>,
  vocabulary: Vocabulary,
  kind: BuildingKind,
  at: Axial,
  /** What is already built on a cell — what it is, whose it is, how far up. */
  standing: (cell: Axial) => Standing | undefined = () => undefined,
  /** Oś skrzydeł, u budynku, który je ma. */
  axis?: number
): true | Refusal {
  for (const spot of footprint(kind, at, axis)) {
    const answer = checkGround(world, vocabulary, kind, spot, standing);
    if (answer !== true) return answer;
  }
  return true;
}

/**
 * Warunki gruntu na jednym polu — to, czym `checkSite` było, zanim budynek mógł
 * zająć więcej niż jedno.
 */
function checkGround(
  world: Map<number, HexCellData>,
  vocabulary: Vocabulary,
  kind: BuildingKind,
  at: Axial,
  standing: (cell: Axial) => Standing | undefined
): true | Refusal {
  const middle = world.get(axialKey(at.q, at.r));
  if (!middle) return 'unknown';

  // Odstęp od zabudowy idzie pierwszy, bo pyta rejestru budynków, a nie mapy —
  // tak samo jak po tamtej stronie, gdzie jest to najtańsze pytanie z tych,
  // które ta funkcja zadaje.
  if (crowded(vocabulary, kind, at, standing)) return 'crowded';

  for (const requirement of kind.site) {
    switch (requirement.kind) {
      case 'land':
        if (underWater(middle)) return 'water';
        break;
      case 'reachable': {
        const answer = reachable(world, at, middle);
        if (answer !== true) return answer;
        break;
      }
      case 'flat': {
        const answer = flat(world, at, middle, requirement.radius, requirement.maxDrop);
        if (answer !== true) return answer;
        break;
      }
      case 'shore': {
        const answer = besideWater(world, at);
        if (answer !== true) return answer;
        break;
      }
      case 'edge': {
        const answer = onEdgeOf(world, vocabulary, requirement.object, at);
        if (answer !== true) return answer;
        break;
      }
      case 'besideOwn': {
        const answer = besideOwn(world, at, requirement.any, standing);
        if (answer !== true) return answer;
        break;
      }
    }
  }

  // What is on the cell, and on the ring around it. Left until after the ground
  // rules because those are cheaper and refuse more often.
  const here = whatIsOn(vocabulary, middle, standing(at));
  if (kind.allowedField?.length && !matches(here, kind.allowedField)) return 'wrongField';
  if (matches(here, kind.disallowedField)) return 'blockedField';

  if (kind.allowedNeighbor?.length || kind.disallowedNeighbor?.length) {
    let found = false;
    let unread = false;

    for (let direction = 0; direction < 6; direction += 1) {
      const side = neighbor(at, direction);
      const data = world.get(axialKey(side.q, side.r));
      if (!data) {
        unread = true;
        continue;
      }

      const there = whatIsOn(vocabulary, data, standing(side));
      if (matches(there, kind.disallowedNeighbor)) return 'blockedNeighbour';
      if (matches(there, kind.allowedNeighbor)) found = true;
    }

    if (kind.allowedNeighbor?.length && !found) {
      // Ground nobody has read is not ground with nothing on it: the answer
      // would otherwise flip from "no" to "yes" as the map arrived.
      return unread ? 'unknown' : 'missingNeighbour';
    }
  }

  return true;
}

/**
 * Ku której krawędzi zwróci się budynek z `faces: "water"` — pierwsza krawędź
 * z wodą, z pominięciem krawędzi własnej osi.
 *
 * **Bliźniak `site::facing`, i istnieje po jeden powód**: żeby duch pod kursorem
 * stał tak, jak stanie budynek. Serwer liczy to po swojemu przy stawianiu i jego
 * odpowiedź jest tą, która zostaje zapisana — ta jest zapowiedzią, jak cała
 * reszta tego pliku.
 */
export function waterFacing(
  world: Map<number, HexCellData>,
  at: Axial,
  axis: number | undefined
): number | undefined {
  for (let edge = 0; edge < 6; edge += 1) {
    if (axis !== undefined && edge % 3 === axis % 3) continue;
    const side = neighbor(at, edge);
    const data = world.get(axialKey(side.q, side.r));
    if (data && underWater(data)) return edge;
  }
  return undefined;
}

/**
 * You build beside yourself, never under yourself. Twin of the server's rule.
 *
 * **Przy skrzydłach pierścień ma dziury**: doki stoczni są sąsiadami jej środka,
 * więc stojąc na doku sąsiaduje się z polem budowy i stawiałoby się budynek pod
 * sobą. Reguła jest ta sama, tylko pyta o wszystkie zajmowane pola, a nie
 * o jedno — `spots` domyślnie jest właśnie tym jednym.
 */
export function inBuildReach(standing: Axial, at: Axial, spots: Axial[] = [at]): boolean {
  if (spots.some((spot) => spot.q === standing.q && spot.r === standing.r)) return false;
  return distance(standing, at) === 1;
}

/**
 * You collect from a building you are standing on or beside.
 *
 * The disc, where building uses the ring, and the difference is not an
 * oversight: a building cannot go where you are, so placement has a reason to
 * exclude the cell underneath and this has none. Walking onto your own hut to
 * empty it is what somebody tries first. Twin of the server's rule.
 */
export function inCollectReach(standing: Axial, at: Axial): boolean {
  return distance(standing, at) <= 1;
}

function levels(count: number): string {
  if (count === 1) return 'o 1 poziom';
  const rest = count % 10;
  const teen = count % 100 >= 12 && count % 100 <= 14;
  return rest >= 2 && rest <= 4 && !teen ? `o ${count} poziomy` : `o ${count} poziomów`;
}

/**
 * What a building on a given rung is called.
 *
 * The rung's own label wins where there is one, because that is the whole point
 * of the condition: a camp on its second rung is a *village*, and saying "obóz
 * (poziom 2)" would be the file's own word thrown away.
 */
function nameOfBuilding(vocabulary: Vocabulary, key: string, level: number): string {
  const building = buildingOf(vocabulary.catalogue, key);
  if (!building) return key;
  return building.levels[level - 1]?.label ?? building.label;
}

/** What the entries of a list are called, as one phrase. */
function named(vocabulary: Vocabulary, list: string[] | undefined): string {
  if (!list?.length) return 'czegoś';

  return list
    .map((key) =>
      key.startsWith('BIO_')
        ? (vocabulary.biomes.get(key)?.label ?? key)
        : labelOfObject(vocabulary.objects, key)
    )
    .join(' albo ');
}

/**
 * Why not, in words that name the actual rule.
 *
 * Worded from the building's own entry rather than from the code: the code says
 * which rule failed and the entry says what that rule asks for. "Brakuje
 * surowca w pobliżu" is true of a lumberjack and of a quarry and tells neither
 * of them apart; "potrzebny las obok" is the condition read out loud, and it
 * comes out of the list plus the dictionary — so a new rule needs no new words.
 */
export function hintFor(
  vocabulary: Vocabulary,
  kind: BuildingKind,
  refusal: Nowhere
): string {
  switch (refusal) {
    case 'far':
      return 'podejdź bliżej — stawiasz na polu sąsiadującym z tobą';
    case 'unknown':
      return 'teren jeszcze się wczytuje';
    case 'water':
      return 'na wodzie nic nie stanie';
    case 'unreachable':
      return 'nikt tam nie dojdzie';
    case 'steep': {
      const rule = kind.site.find((r) => r.kind === 'flat');
      const drop = rule && rule.kind === 'flat' ? rule.maxDrop : 0;
      return `grunt wokół za nierówny — sąsiednie pola mogą się różnić ${levels(drop)}`;
    }
    case 'wrongField':
      return `tu nie — trzeba postawić na ${named(vocabulary, kind.allowedField)}`;
    case 'blockedField':
      return `${named(vocabulary, kind.disallowedField)} — nie na tym polu`;
    case 'missingNeighbour':
      return `potrzebne obok: ${named(vocabulary, kind.allowedNeighbor)}`;
    case 'blockedNeighbour':
      return `${named(vocabulary, kind.disallowedNeighbor)} obok — nie tutaj`;
    case 'noWater':
      return 'musi stać przy wodzie — brzeg, nie środek lądu';
    case 'crowded': {
      // Liczba z katalogu, nie z tego zdania: odstęp obowiązuje w obie strony,
      // więc odmówić może i budynek, który stoi obok, i ten, który stawiasz.
      const hexes = kind.clearance;
      return hexes
        ? `za blisko zabudowań — potrzeba ${hexes} pól pustki wokół`
        : 'za blisko czegoś, co pilnuje pustki wokół siebie';
    }
    case 'unsupported': {
      // Named from the catalogue, so the hint follows the file: the day a
      // second building grows out of something else, this sentence is right
      // without being touched.
      const rule = kind.site.find((r) => r.kind === 'besideOwn');
      const wanted =
        rule && rule.kind === 'besideOwn'
          ? rule.any
              .map((entry) => nameOfBuilding(vocabulary, entry.building, entry.level))
              .join(' albo ')
          : 'twoja budowla';
      return `musi stać przy swojej budowli: ${wanted}`;
    }
    case 'noEdge': {
      // Named from the condition rather than written out, so the hint follows
      // the catalogue the day a second building works the rim of something else.
      const rule = kind.site.find((r) => r.kind === 'edge');
      const object = rule && rule.kind === 'edge' ? rule.object : undefined;
      return `musi stać na skraju: ${named(vocabulary, object ? [object] : [])} musi się tu kończyć`;
    }
  }
}
